import { useState, useEffect } from "react";
import { Search, MapPin, Loader2, DollarSign, ChevronRight, AlertTriangle, Shield, ExternalLink, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { calculateBreakdown, formatMoney, SALARY_LEVELS } from "@/utils/taxCalculator";
import { setPageTitle, setCanonical, setMetaDescription, generateFAQSchema, generateBreadcrumbSchema, generateSpeakableSchema } from "@/utils/seo";
import { SidebarAds, BottomAdBanner } from "@/components/SponsorBanners";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { question: "How does the AI city report work?", answer: "Our AI analyzes real-world data for any US city and generates estimated cost of living figures including rent, groceries, transportation, utilities, and healthcare costs. Reports are saved for 90 days and then automatically refreshed." },
  { question: "How accurate is the AI data?", answer: "AI-generated estimates are based on the model's knowledge of real US city data. They provide a strong directional estimate but may vary from actual current costs. Our pre-built top 20 cities use curated data for higher precision." },
  { question: "Why is there a 3 per day limit?", answer: "Each report uses AI processing which has a small cost. The limit ensures the tool stays free for everyone. Reports are saved, so your city will load instantly on your next visit." },
  { question: "Can I use this for any US city?", answer: "Yes! You can search any city in any US state — from major metros to small towns. The AI will generate cost of living estimates regardless of city size." },
];

export default function GenerateCityPage() {
  const [cityName, setCityName] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(false);
  const [cityData, setCityData] = useState(null);
  const [error, setError] = useState("");
  const [cities, setCities] = useState([]);
  const [dailyCount, setDailyCount] = useState(() => {
    const stored = JSON.parse(localStorage.getItem("city_gen_limit") || '{"count":0,"date":""}');
    return stored.date === new Date().toDateString() ? stored.count : 0;
  });

  useEffect(() => {
    setPageTitle("Search Any US City - AI Cost of Living Report");
    setCanonical("/search-city");
    setMetaDescription("Generate a free cost of living report for any US city. AI-powered estimates for rent, groceries, taxes, and affordability. Search from thousands of cities.");
    axios.get(`${API}/cities`).then(r => setCities(r.data)).catch(() => {});
  }, []);

  const canGenerate = dailyCount < 3;

  const handleGenerate = async () => {
    if (!cityName.trim() || !state.trim()) return;
    if (!canGenerate) { setError("Daily limit reached (3/day). Come back tomorrow for more city reports!"); return; }
    setLoading(true);
    setError("");
    setCityData(null);
    try {
      const res = await axios.post(`${API}/generate-city`, { city_name: cityName.trim(), state: state.trim() });
      setCityData(res.data);
      setPageTitle(`Cost of Living in ${res.data.name}, ${res.data.state_code} - AI Report`);
      const newCount = dailyCount + (res.data.ai_generated ? 1 : 0);
      setDailyCount(newCount);
      localStorage.setItem("city_gen_limit", JSON.stringify({ count: newCount, date: new Date().toDateString() }));
    } catch {
      setError("Unable to generate data for this city. Please check the spelling and try again.");
    }
    setLoading(false);
  };

  const expenses = cityData ? [
    { label: "Rent (1BR)", value: cityData.rent_1br },
    { label: "Groceries", value: cityData.groceries },
    { label: "Transportation", value: cityData.transportation },
    { label: "Utilities", value: cityData.utilities },
    { label: "Healthcare", value: cityData.healthcare },
  ] : [];
  const totalMonthly = expenses.reduce((a, e) => a + e.value, 0);

  return (
    <div data-testid="generate-city-page">
      <script type="application/ld+json">{JSON.stringify(generateFAQSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(generateBreadcrumbSchema([
        { name: "Home", url: "/" }, { name: "Search Any City" }
      ]))}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org", "@type": "WebApplication",
        "name": "AffordScope City Search", "url": "https://affordscope.com/search-city",
        "description": "AI-powered cost of living report generator for any US city",
        "applicationCategory": "FinanceApplication", "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
      })}</script>
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema("/search-city", ["Search Any US City"], ["Generate a free AI-powered cost of living report for any US city. Get estimated rent, groceries, taxes, and affordability data instantly."]))}</script>

      {/* Hero */}
      <section className="hero-pattern py-10 md:py-14">
        <div className="container-main max-w-3xl text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <Search className="w-7 h-7 text-emerald-700" />
          </div>
          <h1 className="speakable-headline text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">
            Search Any US City
          </h1>
          <p className="speakable-summary text-base text-slate-500 max-w-lg mx-auto">
            Don't see your city in our top 20? Enter any US city and get an AI-generated cost of living report instantly.
          </p>
          <p className="text-xs text-slate-400 mt-2">{3 - dailyCount} free reports remaining today</p>
        </div>
      </section>

      {/* Main Content + Sidebar */}
      <div className="container-main py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8">
          <div>
            {/* Search Form */}
            <Card className="shadow-lg mb-8" data-testid="city-generator-form">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">City Name</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input type="text" value={cityName} onChange={e => setCityName(e.target.value)}
                        placeholder="e.g. Arvin, Portland, Miami" data-testid="gen-city-name"
                        className="w-full h-14 pl-10 pr-4 text-lg border border-slate-200 rounded-lg focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 bg-white shadow-sm outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">State</label>
                    <input type="text" value={state} onChange={e => setState(e.target.value)}
                      placeholder="e.g. California or CA" data-testid="gen-state"
                      className="w-full h-14 px-4 text-lg border border-slate-200 rounded-lg focus:border-emerald-600 focus:ring-4 focus:ring-emerald-600/10 bg-white shadow-sm outline-none" />
                  </div>
                </div>
                <Button onClick={handleGenerate} disabled={loading || !cityName.trim() || !state.trim() || !canGenerate}
                  className="w-full h-12 text-base bg-emerald-700 hover:bg-emerald-800 shadow-lg shadow-emerald-700/20" data-testid="gen-submit">
                  {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating Report...</> : <><Search className="w-5 h-5 mr-2" /> Generate City Report</>}
                </Button>
                {!canGenerate && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <p className="text-xs text-amber-700">Daily limit reached (3/day). Come back tomorrow for more city reports!</p>
                  </div>
                )}
                {error && <p className="text-sm text-red-600 mt-3 text-center">{error}</p>}
              </CardContent>
            </Card>

            {/* Results */}
            {cityData && (
              <div className="fade-in-up" data-testid="generated-city-result">
                <Card className="shadow-lg mb-6">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900">{cityData.name}, {cityData.state_code || cityData.state}</h2>
                        {cityData.description && <p className="text-sm text-slate-500 mt-1">{cityData.description}</p>}
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-bold px-2 py-1 rounded-md ${cityData.col_index <= 100 ? 'bg-emerald-100 text-emerald-700' : cityData.col_index <= 130 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                          COL: {cityData.col_index}
                        </span>
                        {cityData.ai_generated && <p className="text-[10px] text-slate-400 mt-1">AI-generated estimate</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {[
                        { label: "Population", value: cityData.population?.toLocaleString() },
                        { label: "Median Income", value: formatMoney(cityData.median_household_income) },
                        { label: "Home Price", value: formatMoney(cityData.home_price) },
                      ].map((s, i) => (
                        <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                          <p className="text-base font-bold font-mono text-slate-800">{s.value}</p>
                        </div>
                      ))}
                    </div>

                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Monthly Cost Breakdown</h3>
                    {expenses.map((e, i) => (
                      <div key={i} className="mb-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">{e.label}</span>
                          <span className="font-mono text-slate-700">{formatMoney(e.value)}</span>
                        </div>
                        <Progress value={Math.min((e.value / (cityData.rent_1br || 1)) * 100, 100)} className="h-2" />
                      </div>
                    ))}
                    <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between font-semibold">
                      <span className="text-slate-900">Total Monthly</span>
                      <span className="font-mono text-slate-900">{formatMoney(totalMonthly)}</span>
                    </div>

                    <h3 className="text-sm font-semibold text-slate-900 mt-6 mb-3">Salary Breakdown</h3>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-xs" data-testid="ai-salary-table">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold text-slate-700">Salary</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-700">Take-Home</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-700">Remaining</th>
                            <th className="text-right px-3 py-2 font-semibold text-slate-700">Verdict</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[50000, 70000, 80000, 100000, 120000, 150000].map(sal => {
                            const b = calculateBreakdown(sal, cityData);
                            const vc = b.verdict === "Comfortable" ? "text-emerald-700 bg-emerald-50" : b.verdict === "Tight" ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
                            return (
                              <tr key={sal} className="border-b border-slate-50">
                                <td className="px-3 py-2 font-mono">{formatMoney(sal)}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(b.net_monthly)}</td>
                                <td className="px-3 py-2 text-right font-mono">{formatMoney(b.remaining_monthly)}</td>
                                <td className="px-3 py-2 text-right"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${vc}`}>{b.verdict}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Apartment Affiliate Banner */}
            <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
              <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl my-6 flex items-center gap-4 hover:shadow-md transition-all" data-testid="post-result-banner">
                <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
                  <p className="text-sm font-semibold text-slate-900">Found Your City? Get $250 Off Your First Furnished Apartment</p>
                  <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
                </div>
                <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
              </div>
            </a>

            {/* City Spotlight — Browse our pre-built cities */}
            {cities.length > 0 && (() => {
              const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
              const spotlightCity = cities[weekNum % cities.length];
              if (!spotlightCity) return null;
              return (
                <section className="mt-8 mb-6" data-testid="search-city-spotlight">
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-semibold">City Spotlight</Badge>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">This Week: {spotlightCity.name}, {spotlightCity.state_code}</h3>
                    <p className="text-xs text-slate-500 mb-3">
                      Rent: {formatMoney(spotlightCity.rent_1br)}/mo | COL Index: {spotlightCity.col_index} {spotlightCity.state_tax_rate === 0 ? '| No state tax' : ''}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/cost-of-living-in-${spotlightCity.slug}`} className="no-underline">
                        <Button size="sm" variant="outline">Explore {spotlightCity.name} <ArrowRight className="w-3 h-3 ml-1" /></Button>
                      </Link>
                      {[60, 80, 100].map(sal => (
                        <Link key={sal} to={`/${sal}k-salary-in-${spotlightCity.slug}`} className="no-underline">
                          <Badge variant="secondary" className="cursor-pointer hover:bg-emerald-50 hover:text-emerald-700 transition-colors">${sal}K</Badge>
                        </Link>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3 text-center">Or browse all <Link to="/" className="text-emerald-600 hover:underline">50 pre-built cities</Link></p>
                </section>
              );
            })()}

            {/* FAQ */}
            <section className="mt-6" data-testid="search-city-faq">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4 data-[state=open]:bg-slate-50">
                    <AccordionTrigger className="text-sm font-medium text-slate-800 hover:no-underline py-4">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 pb-4">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>

            <FinancialDisclaimer />

            <div className="text-center mt-6">
              <Link to="/"><Button variant="outline"><DollarSign className="w-4 h-4 mr-2" /> Back to Salary Calculator</Button></Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden lg:block" data-testid="search-city-sidebar">
            <SidebarAds />
          </div>
        </div>
      </div>

      <BottomAdBanner />
    </div>
  );
}
